import { BooleanInput } from '@angular/cdk/coercion';
import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { TranslocoModule } from '@ngneat/transloco';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { AuthService } from 'app/core/auth/auth.service';
import { SessionExpiryService } from 'app/core/auth/session-expiry.service';
import { UserDataService } from 'app/core/datas/user-data';
import { Project, Site, User } from 'app/core/models/base.model';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [MatButtonModule, MatMenuModule, NgIf, MatIconModule, MatDividerModule, TranslocoModule]
})
export class UserComponent implements OnInit, OnDestroy {
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _router = inject(Router);
    private _authSvc = inject(AuthService);
    private _userDataSvc = inject(UserDataService);
    private _sessionExpirySvc = inject(SessionExpiryService);
    private _fuseConfirmationSvc = inject(FuseConfirmationService);

    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    @Input() site: Site;
    @Input() project: Project;
    @Input() showHQ: boolean = true;
    user: User;
    hasAppPerm: boolean = false;

    /**
     * Session-expiry test tools. `localDev` is only set in environment.ts, so these
     * stay hidden in staging and production (both of which have production: false /
     * true respectively but no localDev flag).
     */
    isDev: boolean = !!environment['localDev'];

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor() {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.user = this._userDataSvc.getUser();
        this.hasAppPerm = this._authSvc.hasBasicRole('app_owner');
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    clickProfile() {
        if (this.hasAppPerm) {
            return;
        }
        this._router.navigate(['account', 'profile']);
    }

    clickProject() {
        this._router.navigate(['a', environment.appUUID]);
    }

    clickApp() {
        this._router.navigate(['admin', 'projects']);
    }

    clickWorkspace() {
        if (this._authSvc.hasBasicRole('app_owner')) {
            this._router.navigate(['admin', 'projects']);

        } else if (this._authSvc.hasBasicRole('project_owner')) {
            this._router.navigate(['select', 'projects']);
        } else if (this._authSvc.hasBasicRole('company')) {
            this._router.navigate(['select', 'companies']);
        } else if (this._authSvc.hasBasicRole('site')) {
            this._router.navigate(['select', 'sites']);
        }

    }

    /**
     * Sign out
     */
    signOut(): void {
        this._router.navigate(['/sign-out']);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Session expiry test tools (dev builds only)
    // -----------------------------------------------------------------------------------------------------

    /**
     * Call the refresh endpoint right now and report what came back.
     */
    devRefreshToken(): void {
        const before = this._authSvc.getTokenExpiresAt();

        this._authSvc.refreshAuthToken().subscribe({
            next: () => {
                const after = this._authSvc.getTokenExpiresAt();
                this.devAlert(
                    'Refresh OK',
                    `Expiry moved from ${this.devFormatTs(before)} to ${this.devFormatTs(after)} (+${Math.round(after - before)}s).`
                );
            },
            error: (err) => {
                console.error('[dev] refresh failed:', err);
                const status = err?.httpErrorResponse?.status ?? err?.status ?? '-';
                this.devAlert(
                    'Refresh FAILED',
                    `HTTP ${status}: ${err?.message ?? err}. Check the console and the Network tab.`
                );
            },
        });
    }

    /**
     * Move the stored expiry to 10s inside the 5-minute warning window.
     * Persisting the credential reschedules the watchdog, so the silent refresh
     * should fire ~10 seconds later with no dialog.
     */
    devSimulateNearExpiry(): void {
        const cred = this._authSvc.getAuthCredential();
        if (!cred) {
            this.devAlert('No credential', 'Not signed in.');
            return;
        }

        // 5 min warning lead + 10s, so the watchdog fires shortly after this returns.
        cred.tokenExpires = Math.floor(new Date().getTime() / 1000) + 310;
        cred.rawJson['expire_ts'] = cred.tokenExpires;
        this._authSvc.setAuthCredential(cred);

        this.devAlert(
            'Near-expiry armed',
            'Silent refresh should fire in ~10s. Watch the Network tab for grant_type=refresh_token - no dialog should appear.'
        );
    }

    /**
     * Same as above, but corrupts the refresh token first so the refresh fails
     * and the expiry dialog is forced to show.
     */
    devSimulateRefreshFailure(): void {
        const cred = this._authSvc.getAuthCredential();
        if (!cred) {
            this.devAlert('No credential', 'Not signed in.');
            return;
        }

        cred.refreshToken = 'dev-invalid-refresh-token';
        cred.rawJson['refresh_token'] = cred.refreshToken;
        cred.tokenExpires = Math.floor(new Date().getTime() / 1000) + 310;
        cred.rawJson['expire_ts'] = cred.tokenExpires;
        this._authSvc.setAuthCredential(cred);

        this.devAlert(
            'Failure armed',
            'Refresh token corrupted. In ~10s the refresh should be rejected and the expiry dialog should appear, counting down from 5:00.'
        );
    }

    private devFormatTs(ts: number): string {
        return ts ? new Date(ts * 1000).toLocaleTimeString() : '-';
    }

    private devAlert(title: string, message: string): void {
        this._fuseConfirmationSvc.open({
            title: title,
            message: message,
            icon: { show: true, name: 'heroicons_outline:beaker', color: 'info' },
            actions: { confirm: { show: true, label: 'OK', color: 'primary' }, cancel: { show: false } },
            dismissible: true,
        });
    }
}
